import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '@/firebase';
import {
    Block,
    Book,
    Chapter,
    ScaleStep,
    ScaleTone,
    Section,
    listAllBooks,
    listBlocksForBook,
    listChaptersForBook,
    listSectionsForBook,
} from '@/utils/bookStructure';

const SCALE_TONE_COLOR: Record<ScaleTone, string> = {
    neutral: '#a8a29e',
    warning: '#c1734e',
    danger: '#332c26',
};

type FlatPage = {
    chapter: Chapter;
    section: Section;
    partLabel: string;
};

const sortByOrder = <T extends { order: number }>(items: T[]) => [...items].sort((a, b) => a.order - b.order);

function buildFlatPages(chapters: Chapter[], sections: Section[]): FlatPage[] {
    const sortedChapters = [...chapters].sort((a, b) => {
        const partDiff = (a.partOrder ?? 0) - (b.partOrder ?? 0);
        if (partDiff !== 0) return partDiff;
        return a.order - b.order;
    });
    const flat: FlatPage[] = [];
    for (const chapter of sortedChapters) {
        const chapterSections = sortByOrder(sections.filter((s) => s.chapterRef.id === chapter.id));
        for (const section of chapterSections) {
            flat.push({ chapter, section, partLabel: chapter.partLabel ?? '' });
        }
    }
    return flat;
}

function ImageBlockView({
    block,
    bookId,
    onUploaded,
}: {
    block: Block;
    bookId: string;
    onUploaded: (blockId: string, url: string) => void;
}) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const content = block.content || {};
    const url = String((content as any).url || '');
    const caption = String((content as any).caption || '');
    const alt = String((content as any).alt || '');

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        setError('');
        try {
            const fileRef = storageRef(storage, `books/${bookId}/blocks/${block.id}/${file.name}`);
            await uploadBytes(fileRef, file, { contentType: file.type });
            const downloadUrl = await getDownloadURL(fileRef);
            await updateDoc(doc(db, 'blocks', block.id), {
                content: { ...content, url: downloadUrl },
                updatedAt: new Date(),
            });
            onUploaded(block.id, downloadUrl);
        } catch (err) {
            console.error('Failed to upload image:', err);
            setError('アップロードに失敗しました。');
        } finally {
            setUploading(false);
        }
    };

    if (!url) {
        return (
            <div className="mb-4 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[#c9b8a4] bg-[#f6efe4] px-4 py-8 text-center">
                <i className="ri-image-2-line text-2xl text-[#a35c3a]" />
                <p className="text-xs text-[#8a7a68]">{caption || '画像をアップロードしてください'}</p>
                <label className="cursor-pointer rounded-lg bg-[#c1734e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#a35c3a]">
                    {uploading ? 'アップロード中...' : '画像をアップロード'}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={handleFileChange} />
                </label>
                {error && <p className="text-xs text-red-500">{error}</p>}
            </div>
        );
    }
    return (
        <figure className="mb-4">
            <img src={url} alt={alt} draggable={false} className="w-full rounded-lg border border-[#e4d9cc]" />
            {caption && <figcaption className="mt-1 text-xs text-[#8a7a68]">{caption}</figcaption>}
        </figure>
    );
}

function BlockView({
    block,
    bookId,
    onImageUploaded,
}: {
    block: Block;
    bookId: string;
    onImageUploaded: (blockId: string, url: string) => void;
}) {
    const content = block.content || {};
    if (block.type === 'paragraph') {
        return <p className="mb-4 text-[15px] leading-[1.95] text-[#332c26]">{String((content as any).text || '')}</p>;
    }
    if (block.type === 'subheading') {
        const level = Number((content as any).level) || 4;
        const text = String((content as any).text || '');
        if (level <= 4) {
            return (
                <h4 className="mt-8 mb-3 inline-block rounded-full bg-[#f0e6d8] px-3 py-1 text-[13px] font-bold tracking-wide text-[#a35c3a]">
                    {text}
                </h4>
            );
        }
        return <h5 className="mt-6 mb-2 text-[16.5px] font-bold text-[#332c26]">{text}</h5>;
    }
    if (block.type === 'list') {
        const items: string[] = Array.isArray((content as any).items) ? (content as any).items : [];
        return (
            <ul className="mb-4 list-disc space-y-1 pl-5 text-[15px] leading-[1.9] text-[#332c26]">
                {items.map((item, i) => (
                    <li key={i}>{item}</li>
                ))}
            </ul>
        );
    }
    if (block.type === 'table') {
        // Firestoreは配列の直接ネストを許可しないため、行は {cells: string[]} でラップして保存している
        const rawRows = Array.isArray((content as any).rows) ? (content as any).rows : [];
        const rows: string[][] = rawRows.map((row: any) => (Array.isArray(row) ? row : Array.isArray(row?.cells) ? row.cells : []));
        return (
            <div className="mb-4 overflow-x-auto rounded-lg border border-[#e4d9cc]">
                <table className="w-full border-collapse text-sm">
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri} className="border-b border-[#e4d9cc] last:border-0">
                                {row.map((cell, ci) => (
                                    <td key={ci} className="px-3 py-2 align-top text-[#332c26]">
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    if (block.type === 'image') {
        return <ImageBlockView block={block} bookId={bookId} onUploaded={onImageUploaded} />;
    }
    if (block.type === 'scale') {
        const steps: ScaleStep[] = Array.isArray((content as any).steps) ? (content as any).steps : [];
        if (steps.length === 0) return null;
        return (
            <div className="mb-6 mt-2 px-2">
                <div className="flex items-start">
                    {steps.map((step, i) => (
                        <div key={i} className="flex flex-1 items-center">
                            <div className="flex flex-col items-center gap-2">
                                <div
                                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-center text-[11px] font-bold leading-tight text-white shadow-sm"
                                    style={{ backgroundColor: SCALE_TONE_COLOR[step.tone] || SCALE_TONE_COLOR.neutral }}
                                >
                                    {step.value}
                                </div>
                                <span className="whitespace-nowrap text-xs text-[#332c26]">{step.label}</span>
                            </div>
                            {i < steps.length - 1 && (
                                <div
                                    className="mb-6 h-[3px] flex-1"
                                    style={{
                                        background: `linear-gradient(to right, ${
                                            SCALE_TONE_COLOR[step.tone] || SCALE_TONE_COLOR.neutral
                                        }, ${SCALE_TONE_COLOR[steps[i + 1].tone] || SCALE_TONE_COLOR.neutral})`,
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    if (block.type === 'note') {
        return (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {String((content as any).text || '')}
            </div>
        );
    }
    return null;
}

export default function TextWebPreviewPage() {
    const [books, setBooks] = useState<Book[]>([]);
    const [selectedBookId, setSelectedBookId] = useState<string>('');
    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [sections, setSections] = useState<Section[]>([]);
    const [blocks, setBlocks] = useState<Block[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTocOpen, setIsTocOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        listAllBooks().then((result) => {
            setBooks(result);
            if (result.length > 0 && !selectedBookId) {
                setSelectedBookId(result[0].id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedBookId) return;
        setLoading(true);
        const bookRef = doc(db, 'books', selectedBookId);
        Promise.all([
            listChaptersForBook(bookRef),
            listSectionsForBook(bookRef),
            listBlocksForBook(bookRef),
        ])
            .then(([chapterList, sectionList, blockList]) => {
                setChapters(chapterList);
                setSections(sectionList);
                setBlocks(blockList);
                setCurrentIndex(0);
            })
            .finally(() => setLoading(false));
    }, [selectedBookId]);

    const flatPages = useMemo(() => buildFlatPages(chapters, sections), [chapters, sections]);
    const currentPage = flatPages[currentIndex];
    const currentBlocks = useMemo(
        () => (currentPage ? sortByOrder(blocks.filter((b) => b.sectionRef.id === currentPage.section.id)) : []),
        [blocks, currentPage]
    );

    const groupedToc = useMemo(() => {
        const sortedChapters = [...chapters].sort((a, b) => {
            const partDiff = (a.partOrder ?? 0) - (b.partOrder ?? 0);
            if (partDiff !== 0) return partDiff;
            return a.order - b.order;
        });
        const parts = new Map<string, Chapter[]>();
        for (const chapter of sortedChapters) {
            const key = chapter.partLabel ?? '';
            if (!parts.has(key)) parts.set(key, []);
            parts.get(key)!.push(chapter);
        }
        return Array.from(parts.entries());
    }, [chapters]);

    const handleImageUploaded = (blockId: string, url: string) => {
        setBlocks((prev) =>
            prev.map((b) => (b.id === blockId ? { ...b, content: { ...b.content, url } } : b))
        );
    };

    const goToSection = (sectionId: string) => {
        const idx = flatPages.findIndex((p) => p.section.id === sectionId);
        if (idx >= 0) {
            setCurrentIndex(idx);
            setIsTocOpen(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-bold text-gray-900">テキストWebプレビュー</h2>
                <p className="text-sm text-gray-500">
                    PDFから変換した冊子データをスマホ表示でプレビューします（管理者のみ閲覧可能）。
                </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
                <label className="text-sm font-semibold text-gray-600">冊子を選択</label>
                <select
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    value={selectedBookId}
                    onChange={(e) => setSelectedBookId(e.target.value)}
                >
                    {books.map((book) => (
                        <option key={book.id} value={book.id}>
                            {book.title} {book.isPublished ? '' : '(下書き)'}
                        </option>
                    ))}
                </select>
                {loading && <span className="text-xs text-gray-400">読み込み中...</span>}
                {!loading && flatPages.length > 0 && (
                    <span className="text-xs text-gray-400">
                        全{flatPages.length}ページ中 {currentIndex + 1}ページ目
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-4 lg:flex-row">
                <div className="w-full max-w-xs shrink-0 rounded-xl border border-gray-200 bg-white p-3 lg:block">
                    <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-gray-400">目次</p>
                    <div className="max-h-[70vh] space-y-3 overflow-y-auto">
                        {groupedToc.map(([partLabel, chs]) => (
                            <div key={partLabel}>
                                <p className="mb-1 px-1 text-xs font-bold text-[#a35c3a]">{partLabel}</p>
                                {chs.map((chapter) => (
                                    <div key={chapter.id} className="mb-2">
                                        <p className="px-1 text-xs font-semibold text-gray-600">{chapter.title}</p>
                                        <ul className="mt-1 space-y-0.5">
                                            {sortByOrder(sections.filter((s) => s.chapterRef.id === chapter.id)).map(
                                                (section) => (
                                                    <li key={section.id}>
                                                        <button
                                                            type="button"
                                                            onClick={() => goToSection(section.id)}
                                                            className={`w-full rounded-md px-2 py-1 text-left text-xs ${
                                                                currentPage?.section.id === section.id
                                                                    ? 'bg-emerald-50 font-semibold text-emerald-600'
                                                                    : 'text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {section.title}
                                                        </button>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ))}
                        {groupedToc.length === 0 && <p className="px-1 text-xs text-gray-400">データがありません</p>}
                    </div>
                </div>

                <div className="flex flex-1 justify-center">
                    <div className="w-full max-w-[390px]">
                        <div className="overflow-hidden rounded-[36px] border-8 border-gray-800 bg-black shadow-xl">
                            <div
                                className="h-[720px] overflow-y-auto bg-[#faf7f2]"
                                onContextMenu={(e) => e.preventDefault()}
                                style={{ userSelect: 'none' }}
                            >
                                {currentPage ? (
                                    <article className="px-5 py-6">
                                        <p className="mb-1 text-[11px] font-bold tracking-wide text-[#c1734e]">
                                            {currentPage.partLabel}
                                        </p>
                                        <p className="mb-4 text-xs text-[#8a7a68]">{currentPage.chapter.title}</p>
                                        <h1 className="mb-6 border-b border-[#e4d9cc] pb-4 text-[22px] font-bold leading-[1.4] text-[#332c26]">
                                            {currentPage.section.title}
                                        </h1>
                                        {currentBlocks.map((block) => (
                                            <BlockView
                                                key={block.id}
                                                block={block}
                                                bookId={selectedBookId}
                                                onImageUploaded={handleImageUploaded}
                                            />
                                        ))}
                                        {currentBlocks.length === 0 && (
                                            <p className="text-sm text-gray-400">このページにはまだ内容がありません。</p>
                                        )}
                                    </article>
                                ) : (
                                    <div className="flex h-full items-center justify-center text-sm text-gray-400">
                                        冊子を選択してください
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-2">
                            <button
                                type="button"
                                disabled={currentIndex <= 0}
                                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 disabled:opacity-40"
                            >
                                ← 前のページ
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsTocOpen((v) => !v)}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 lg:hidden"
                            >
                                目次
                            </button>
                            <button
                                type="button"
                                disabled={currentIndex >= flatPages.length - 1}
                                onClick={() => setCurrentIndex((i) => Math.min(flatPages.length - 1, i + 1))}
                                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 disabled:opacity-40"
                            >
                                次のページ →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
